import type { ChatAttachmentDraft, ChatUploadState } from "../types";
import type { MessageRow } from "../../lib/db";
import type { PinnedMessageListItem } from "../../lib/authApi";
import type { ChatReplyDraft, MessageReactionBadge } from "./domainTypes";

export type ConversationRef =
  | { kind: "dm"; peerId: string }
  | { kind: "workspace-topic"; workspaceId: string; topicId: string }
  | { kind: "vault"; vaultId: string };

export function conversationRefsEqual(a: ConversationRef | null, b: ConversationRef | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === "dm") return b.kind === "dm" && a.peerId === b.peerId;
  if (a.kind === "vault") return b.kind === "vault" && a.vaultId === b.vaultId;
  return (
    b.kind === "workspace-topic" &&
    a.workspaceId === b.workspaceId &&
    a.topicId === b.topicId
  );
}

export function dmPeerIdFromConversation(ref: ConversationRef | null): string | null {
  return ref?.kind === "dm" ? ref.peerId : null;
}

export function vaultIdFromConversation(ref: ConversationRef | null): string | null {
  return ref?.kind === "vault" ? ref.vaultId : null;
}

export type ChatState = {
  lastError: string | null;
  decryptError: string | null;
  selectedConversation: ConversationRef | null;
  conversationLoading: boolean;
  messages: MessageRow[];
  chatHasMoreOlder: boolean;
  chatInput: string;
  chatAttachmentDraft: ChatAttachmentDraft | null;
  chatUpload: ChatUploadState | null;
  chatReplyTo: ChatReplyDraft | null;
  /** Workspace topic: серверные реакции по id сообщения (не хранятся как отдельные MessageRow). */
  workspaceReactionsByMessageId: Record<string, MessageReactionBadge[]>;
  /** Закреплённые сообщения для текущего контекста (workspace topic / DM / vault). */
  pinnedMessages: PinnedMessageListItem[];
};

export type ChatAction =
  | { type: "set-error"; error: string | null }
  | { type: "set-decrypt-error"; error: string | null }
  | { type: "set-selected-conversation"; conversation: ConversationRef | null }
  | { type: "set-conversation-loading"; loading: boolean }
  | {
      type: "set-messages";
      messages: MessageRow[];
      hasMoreOlder?: boolean;
      conversationLoading?: boolean;
      workspaceReactionsByMessageId?: Record<string, MessageReactionBadge[]>;
    }
  | {
      type: "prepend-messages";
      messages: MessageRow[];
      hasMoreOlder: boolean;
      workspaceReactionsMerge?: Record<string, MessageReactionBadge[]>;
    }
  | { type: "patch-message"; messageId: string; patch: Partial<MessageRow> }
  | { type: "upsert-message"; message: MessageRow }
  /** Replace optimistic temp id (e.g. workspace outbox) with server message row. */
  | { type: "replace-message-id"; oldId: string; message: MessageRow }
  | { type: "set-chat-input"; value: string }
  | { type: "set-chat-attachment-draft"; draft: ChatAttachmentDraft | null }
  | { type: "set-chat-upload"; upload: ChatUploadState | null }
  | { type: "set-chat-reply-to"; draft: ChatReplyDraft | null }
  | {
      type: "apply-workspace-reaction";
      messageId: string;
      userId: string;
      emoji: string;
      action: "add" | "remove";
      currentUserId: string;
    }
  | { type: "remove-message"; messageId: string }
  | { type: "set-pinned-messages"; pins: PinnedMessageListItem[] }
  | { type: "add-pinned-message"; item: PinnedMessageListItem }
  | { type: "remove-pinned-message"; messageId: string }
  | { type: "reset" };

export function initialChatState(): ChatState {
  return {
    lastError: null,
    decryptError: null,
    selectedConversation: null,
    conversationLoading: false,
    messages: [],
    chatHasMoreOlder: false,
    chatInput: "",
    chatAttachmentDraft: null,
    chatUpload: null,
    chatReplyTo: null,
    workspaceReactionsByMessageId: {},
    pinnedMessages: [],
  };
}

function sortReactionBadges(list: MessageReactionBadge[]): MessageReactionBadge[] {
  return [...list].sort((a, b) => {
    if (a.ownSelected !== b.ownSelected) return a.ownSelected ? -1 : 1;
    if (a.count !== b.count) return b.count - a.count;
    return a.emoji.localeCompare(b.emoji);
  });
}

function patchWorkspaceReactionsMap(
  prev: Record<string, MessageReactionBadge[]>,
  messageId: string,
  userId: string,
  emoji: string,
  action: "add" | "remove",
  currentUserId: string,
): Record<string, MessageReactionBadge[]> {
  const list = [...(prev[messageId] ?? [])];
  const idx = list.findIndex((b) => b.emoji === emoji);
  let badge = idx >= 0 ? { ...list[idx]! } : null;

  if (action === "add") {
    if (!badge) {
      list.push({ emoji, count: 1, ownSelected: userId === currentUserId });
    } else {
      badge.count += 1;
      if (userId === currentUserId) badge.ownSelected = true;
      list[idx] = badge;
    }
  } else {
    if (!badge || badge.count <= 0) return prev;
    badge.count = Math.max(0, badge.count - 1);
    if (userId === currentUserId) badge.ownSelected = false;
    if (badge.count === 0) {
      list.splice(idx, 1);
    } else {
      list[idx] = badge;
    }
  }

  const next = { ...prev, [messageId]: sortReactionBadges(list) };
  if (list.length === 0) {
    delete next[messageId];
  }
  return next;
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "set-error":
      return { ...state, lastError: action.error };
    case "set-decrypt-error":
      return { ...state, decryptError: action.error };
    case "set-selected-conversation": {
      const prev = state.selectedConversation;
      const next = action.conversation;
      if (conversationRefsEqual(prev, next)) return state;
      const switched = true;
      return {
        ...state,
        selectedConversation: next,
        conversationLoading: !!next,
        chatInput: next ? (switched ? "" : state.chatInput) : "",
        chatAttachmentDraft: next ? (switched ? null : state.chatAttachmentDraft) : null,
        chatUpload: next ? (switched ? null : state.chatUpload) : null,
        chatReplyTo: next ? (switched ? null : state.chatReplyTo) : null,
        messages: next ? (switched ? [] : state.messages) : [],
        chatHasMoreOlder: next ? (switched ? false : state.chatHasMoreOlder) : false,
        workspaceReactionsByMessageId: next ? (switched ? {} : state.workspaceReactionsByMessageId) : {},
        pinnedMessages: next ? (switched ? [] : state.pinnedMessages) : [],
      };
    }
    case "set-conversation-loading":
      return { ...state, conversationLoading: action.loading };
    case "set-messages":
      return {
        ...state,
        conversationLoading: action.conversationLoading ?? false,
        messages: action.messages,
        chatHasMoreOlder: action.hasMoreOlder ?? false,
        workspaceReactionsByMessageId: action.workspaceReactionsByMessageId ?? {},
      };
    case "prepend-messages": {
      const byId = new Map<string, MessageRow>();
      for (const m of action.messages) byId.set(m.id, m);
      for (const m of state.messages) byId.set(m.id, m);
      const merged = [...byId.values()].sort((a, b) =>
        a.created_at !== b.created_at ? a.created_at - b.created_at : a.id.localeCompare(b.id),
      );
      return {
        ...state,
        messages: merged,
        chatHasMoreOlder: action.hasMoreOlder,
        workspaceReactionsByMessageId: {
          ...state.workspaceReactionsByMessageId,
          ...(action.workspaceReactionsMerge ?? {}),
        },
      };
    }
    case "patch-message":
      return {
        ...state,
        messages: state.messages.map((message) =>
          message.id === action.messageId ? { ...message, ...action.patch } : message,
        ),
      };
    case "upsert-message": {
      const next = [...state.messages.filter((message) => message.id !== action.message.id), action.message];
      next.sort((a, b) =>
        a.created_at !== b.created_at ? a.created_at - b.created_at : a.id.localeCompare(b.id),
      );
      return { ...state, messages: next };
    }
    case "replace-message-id": {
      const next = [
        ...state.messages.filter(
          (message) => message.id !== action.oldId && message.id !== action.message.id,
        ),
        action.message,
      ];
      next.sort((a, b) =>
        a.created_at !== b.created_at ? a.created_at - b.created_at : a.id.localeCompare(b.id),
      );
      const replyDraft = state.chatReplyTo;
      const migratedReply =
        replyDraft?.messageId === action.oldId
          ? { ...replyDraft, messageId: action.message.id }
          : replyDraft;
      return { ...state, messages: next, chatReplyTo: migratedReply };
    }
    case "set-chat-input":
      return { ...state, chatInput: action.value };
    case "set-chat-attachment-draft":
      return { ...state, chatAttachmentDraft: action.draft };
    case "set-chat-upload":
      return { ...state, chatUpload: action.upload };
    case "set-chat-reply-to":
      return { ...state, chatReplyTo: action.draft };
    case "apply-workspace-reaction":
      return {
        ...state,
        workspaceReactionsByMessageId: patchWorkspaceReactionsMap(
          state.workspaceReactionsByMessageId,
          action.messageId,
          action.userId,
          action.emoji,
          action.action,
          action.currentUserId,
        ),
      };
    case "remove-message":
      return {
        ...state,
        messages: state.messages.filter((m) => m.id !== action.messageId),
      };
    case "set-pinned-messages":
      return { ...state, pinnedMessages: action.pins };
    case "add-pinned-message": {
      const rest = state.pinnedMessages.filter((p) => p.message_id !== action.item.message_id);
      return { ...state, pinnedMessages: [...rest, action.item] };
    }
    case "remove-pinned-message":
      return {
        ...state,
        pinnedMessages: state.pinnedMessages.filter((p) => p.message_id !== action.messageId),
      };
    case "reset":
      return initialChatState();
    default:
      return state;
  }
}
